/**
 * screens/rules/Rules.tsx — the home screen.
 * Lists every rule with inline activate/deactivate, motion + status + priority,
 * a live accept-rate sparkline, and precedence-aware ordering. UI only — all
 * data work goes through the rules service inside try/catch.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Pressable, Platform, TextInput } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { LightColors as C, FontSize as FS, FontWeight as FW, Radii as R, Shadows as SH, FontFamily as FF } from '../../theme/theme';
import { Txt, Row, Cluster } from '../../components/ui';
import Button from '../../components/button';
import ConfirmDialog from '../../components/confirmDialog';
import SuccessDialog from '../../components/successDialog';
import Chip from '../../components/chip';
import Alert from '../../components/alert';
import Switch from '../../components/switch';
import Empty from '../../components/empty';
import Spinner from '../../components/spinner';
import Sparkline from '../../components/sparkline';
import Icon, { IconName } from '../../components/icon';
import FilterChip from '../../components/filterChip';
import { Page, PageHeader } from '../_layout';

import { Rule, RuleStatus } from '../../schema/rules/rule';
import { listRules, activateRule, deactivateRule, deleteRule, restoreRule } from '../../services/rules/rules';
import { useLiveCatalogRefresh } from '../../lib/liveRefresh';
import { motionMeta, FAMILIES, FamilyKey } from '../../domain/motions';
import { toneColors } from '../../theme/tones';
import { handleApiError } from '../../utils/errorHandler';
import { useCounts } from '../../context/countsContext';
import { useApp } from '../../context/appContext';
import type { AppStackParamList } from '../../navigation/types';

type Nav = NativeStackNavigationProp<AppStackParamList>;

/** Single filter row: 'all' + each motion family + 'deactivated'. */
type RuleFilter = 'all' | FamilyKey | 'manual' | 'deactivated' | 'deleted';

const isWeb = Platform.OS === 'web';

/** Premium header action — hover lifts the button with a growing shadow and a
 *  drifting icon; press settles it back. `ai` = soft accent outline (Let AI
 *  propose); `primary` = filled ink (New rule). */
const HeaderAction: React.FC<{ label: string; icon: IconName; variant: 'ai' | 'primary'; onPress: () => void }> = ({
  label,
  icon,
  variant,
  onPress,
}) => {
  const [hover, setHover] = useState(false);
  const [press, setPress] = useState(false);
  const ai = variant === 'ai';
  const bg = ai ? (hover ? C.accentSoft : C.card) : hover ? C.accentInk : C.ink;
  const border = ai ? (hover ? C.accent : C.line) : hover ? C.accentInk : C.ink;
  const fg = ai ? (hover ? C.accentInk : C.ink) : '#FFFFFF';
  const lift = isWeb && hover && !press;
  const shadow = ai ? '0 8px 20px -6px rgba(16,24,40,0.20)' : '0 12px 26px -8px rgba(16,24,40,0.50)';
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => {
        setHover(false);
        setPress(false);
      }}
      onPressIn={() => setPress(true)}
      onPressOut={() => setPress(false)}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        height: 38,
        paddingHorizontal: 16,
        borderRadius: R.r2,
        borderWidth: 1,
        borderColor: border,
        backgroundColor: bg,
        ...(isWeb
          ? ({
              transitionProperty: 'transform, box-shadow, background-color, border-color',
              transitionDuration: '170ms',
              transitionTimingFunction: 'cubic-bezier(.2,.7,.3,1)',
            } as object)
          : {}),
        ...(lift ? ({ transform: [{ translateY: -2 }], boxShadow: shadow } as object) : {}),
        ...(isWeb && press ? ({ transform: [{ translateY: 0 }] } as object) : {}),
      }}
    >
      <View
        style={{
          ...(isWeb
            ? ({ transitionProperty: 'transform', transitionDuration: '170ms', transitionTimingFunction: 'cubic-bezier(.2,.7,.3,1)' } as object)
            : {}),
          ...(lift ? ({ transform: [{ translateX: ai ? 1 : 0 }, { scale: 1.12 }] } as object) : {}),
        }}
      >
        <Icon name={icon} size={16} color={fg} strokeWidth={ai ? 2 : 2.4} />
      </View>
      <Txt size={FS.md} weight={FW.semibold} color={fg}>
        {label}
      </Txt>
    </Pressable>
  );
};

/** Premium search field — pill, soft shadow, accent focus ring, icon that
 *  tints on focus, and a clear button once there's a query. */
const SearchBar: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [focused, setFocused] = useState(false);
  return (
    <View style={{ maxWidth: 440, minWidth: 220 }}>
      <View
        style={{
          height: 46,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 11,
          paddingHorizontal: 15,
          borderRadius: R.full,
          backgroundColor: C.card,
          borderWidth: 1.5,
          borderColor: C.ink,
          ...(isWeb
            ? ({ transitionProperty: 'border-color, box-shadow', transitionDuration: '160ms', transitionTimingFunction: 'cubic-bezier(.2,.7,.3,1)' } as object)
            : {}),
          ...((focused
            ? { boxShadow: `0 0 0 3px ${C.accentSoft}, 0 10px 24px -10px rgba(16,24,40,0.30)` }
            : SH.sm) as object),
        }}
      >
        <Icon name="search" size={17} color={focused ? C.accentInk : C.ink4} strokeWidth={2.2} />
        <TextInput
          value={value}
          onChangeText={onChange}
          placeholder="Search rules by name or trigger…"
          placeholderTextColor={C.ink4}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1,
            height: '100%',
            color: C.ink,
            fontSize: FS.md,
            fontFamily: FF.sans,
            fontWeight: FW.regular,
            ...(isWeb ? ({ outlineStyle: 'none' } as object) : {}),
          }}
        />
        {value.length > 0 ? (
          <Pressable
            onPress={() => onChange('')}
            hitSlop={8}
            style={{ width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center', backgroundColor: C.paper2 }}
          >
            <Icon name="x" size={12} color={C.ink2} strokeWidth={2.6} />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
};

const STATUS_RANK: Record<RuleStatus, number> = { active: 0, approved: 0, draft: 1, deactivated: 2 };

interface RateView {
  text: string;
  color: string;
  stroke: string;
  data: number[] | null;
}

function rateView(r: Rule): RateView {
  if (r.status === 'draft' || r.acceptRate == null) return { text: '—', color: C.ink3, stroke: C.ink4, data: null };
  if (r.cold) return { text: 'learning', color: C.ink4, stroke: C.ink4, data: r.trend };
  const rt = r.acceptRate;
  if (rt >= 0.3) return { text: `${Math.round(rt * 100)}%`, color: C.success, stroke: C.success, data: r.trend };
  if (rt >= 0.18) return { text: `${Math.round(rt * 100)}%`, color: C.ink2, stroke: C.info, data: r.trend };
  return { text: `${Math.round(rt * 100)}%`, color: C.warn, stroke: C.warn, data: r.trend };
}

function StatusPill({ status }: { status: RuleStatus }) {
  switch (status) {
    case 'active':
      return <Chip tone="success" label="Active" />;
    case 'approved':
      return <Chip tone="info" label="Approved" />;
    case 'deactivated':
      return <Chip tone="neutral" outline label="Off" />;
    default:
      return <Chip label="Draft" />;
  }
}

/** Render a sentence with the given category words emphasised (bold ink) so the
 *  key words pop, while the connective words stay quiet — the operator's eye
 *  lands on "Mains" / "Desserts" instantly. */
function highlightWords(text: string, words: string[]): React.ReactNode {
  const real = words.filter(Boolean);
  if (real.length === 0) return text;
  const escaped = real.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(${escaped.join('|')})`, 'g');
  return text.split(re).map((part, i) =>
    real.includes(part) ? (
      <Txt
        key={i}
        weight={FW.semibold}
        color={C.ink}
        style={{ backgroundColor: C.accentSoft, borderRadius: R.r1, paddingHorizontal: 6, paddingVertical: 2 }}
      >
        {part}
      </Txt>
    ) : (
      part
    ),
  );
}

function RuleCard({ rule, onToggle, onOpen, onDelete, onRestore, restorePending = false }: { rule: Rule; onToggle: () => void; onOpen: () => void; onDelete?: () => void; onRestore?: () => void; restorePending?: boolean }) {
  const [hover, setHover] = useState(false);
  const m = motionMeta(rule.motion);
  const rv = rateView(rule);
  const off = rule.status === 'deactivated';

  return (
    <Pressable
      onPress={onOpen}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 16,
          backgroundColor: C.card,
          borderWidth: 1,
          // Dormant cards adopt the amber "out of date" treatment (matches AI Proposals).
          borderColor: rule.dormant ? toneColors('warn').line : hover ? C.lineStrong : C.line,
          borderRadius: R.r4,
          paddingVertical: 18,
          paddingHorizontal: 20,
          opacity: off ? 0.6 : 1,
        },
        (hover ? SH.md : SH.sm) as object,
      ]}
    >
      {/* Accent rail — motion tone, or amber when the rule is out of date */}
      <View style={{ width: 3, alignSelf: 'stretch', borderRadius: R.full, backgroundColor: rule.dormant ? toneColors('warn').fg : toneColors(m.tone).fg, opacity: off ? 0.4 : 0.9 }} />

      {/* Main: the plain sentence leads (the clearest identity), meta chips
          below, and the AI codename demoted to a small muted caption. */}
      <View style={{ flex: 1, minWidth: 0 }}>
        {/* Rule name — a premium field label (accent tick + letterspaced
            caption) over the bold headline. */}
        {rule.name ? (
          <View style={{ marginBottom: 5 }}>
            <Row gap={6} style={{ alignItems: 'center', marginBottom: 3 }}>
              <View style={{ width: 14, height: 2.5, borderRadius: 999, backgroundColor: toneColors(m.tone).fg, opacity: off ? 0.4 : 0.9 }} />
              <Txt size={10} weight={FW.bold} color={C.ink3} style={{ letterSpacing: 1.4, textTransform: 'uppercase' }}>
                Rule name
              </Txt>
            </Row>
            {/* Name + origin badge on one row — AI-suggested gets a premium
                highlighted badge; hand-authored gets a quiet subtle pill. */}
            <Row gap={10} style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Txt size={17.5} weight={FW.bold} color={C.ink} numberOfLines={1} tracking={-0.3} style={{ flexShrink: 1 }}>
                {rule.name}
              </Txt>
              {rule.source === 'ai_proposed' ? (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 5,
                    paddingLeft: 7,
                    paddingRight: 10,
                    paddingVertical: 4,
                    borderRadius: R.full,
                    backgroundColor: C.accentSoft,
                    borderWidth: 1,
                    borderColor: C.accent,
                    ...(SH.sm as object),
                  }}
                >
                  <Icon name="sparkle-fill" size={12} color={C.accent} />
                  <Txt size={11} weight={FW.bold} color={C.accentInk} style={{ letterSpacing: 0.3 }}>
                    Suggested by AI
                  </Txt>
                </View>
              ) : (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 5,
                    paddingLeft: 7,
                    paddingRight: 10,
                    paddingVertical: 4,
                    borderRadius: R.full,
                    backgroundColor: C.paper2,
                    borderWidth: 1,
                    borderColor: C.ink,
                    ...(SH.sm as object),
                  }}
                >
                  <Icon name="pencil" size={12} color={C.ink} />
                  <Txt size={11} weight={FW.bold} color={C.ink} style={{ letterSpacing: 0.3 }}>
                    Created by you
                  </Txt>
                </View>
              )}
            </Row>
          </View>
        ) : null}

        {/* The rule as a sentence — the supporting description, with the
            category words still highlighted so they pop. */}
        <Txt size={14} weight={FW.regular} color={C.ink3} lineHeight={24}>
          {highlightWords(
            rule.intentText ?? `${rule.triggerText} → suggest ${rule.fromText}`,
            (rule.categoryExamples ?? []).map((c) => c.name),
          )}
        </Txt>

        <Row gap={8} style={{ marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip tone={m.tone} dot label={m.label} />
          <StatusPill status={rule.status} />
          {rule.dormant && <Chip tone="warn" dot label="Dormant" />}
          {rule.superseded && <Chip tone="neutral" outline label="superseded by AI" />}
        </Row>

        {/* Out-of-date banner — the SAME warn Alert the AI-Proposals card uses,
            so a dormant rule and a stale proposal read identically. */}
        {rule.dormant && (
          <Alert tone="warn" style={{ marginTop: 12 }} iconSize={15}>
            <Txt size={13.5} color={toneColors('warn').fg} lineHeight={20}>
              <Txt size={13.5} weight={FW.semibold} color={toneColors('warn').fg}>
                Not running.{' '}
              </Txt>
              {(rule.dormantReason ?? 'a catalog item it depends on is no longer available') + '.'}
            </Txt>
          </Alert>
        )}

        {/* Example items — each category as a pill (the key word) + its items. */}
        {rule.categoryExamples && rule.categoryExamples.length > 0 && (
          <View style={{ marginTop: 12, gap: 6 }}>
            {rule.categoryExamples.map((c) => (
              <Row key={c.name} gap={8} style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <View
                  style={{
                    paddingHorizontal: 9,
                    paddingVertical: 3,
                    borderRadius: R.full,
                    backgroundColor: C.paper2,
                    borderWidth: 1,
                    borderColor: C.line,
                  }}
                >
                  <Txt size={11.5} weight={FW.semibold} color={C.ink}>
                    {c.name}
                  </Txt>
                </View>
                <Txt size={12.5} color={C.ink3} style={{ flexShrink: 1 }} numberOfLines={1}>
                  {c.items.join(', ')}
                </Txt>
              </Row>
            ))}
          </View>
        )}
      </View>

      {/* Right: actions (edit + activate toggle) then accept-rate + priority */}
      <View style={{ alignItems: 'flex-end', gap: 10 }}>
        <Row gap={10} style={{ alignItems: 'center' }}>
          {rule.deleted ? (
            /* Deleted rules: a red toggle to restore them (back to Deactivated). */
            <Pressable onPress={(e) => e.stopPropagation()}>
              <Switch value={restorePending} tone="danger" onValueChange={(next) => { if (next) onRestore?.(); }} />
            </Pressable>
          ) : (
            <>
              {/* Edit — opens the rule in the builder */}
              <Pressable
                onPress={(e) => {
                  e.stopPropagation();
                  onOpen();
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: R.r2,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: C.paper2,
                  borderWidth: 1,
                  borderColor: C.line,
                }}
              >
                <Icon name="pencil" size={15} color={C.ink2} />
              </Pressable>
              {/* Delete — soft-delete (moves to the Deleted tab) */}
              {onDelete && (
                <Pressable
                  onPress={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: R.r2,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: toneColors('danger').soft,
                    borderWidth: 1,
                    borderColor: toneColors('danger').line,
                  }}
                >
                  <Icon name="x" size={15} color={toneColors('danger').fg} strokeWidth={2.4} />
                </Pressable>
              )}
              {/* Activate / deactivate — must not open the rule */}
              <Pressable onPress={(e) => e.stopPropagation()}>
                <Switch value={rule.status === 'active'} onValueChange={onToggle} />
              </Pressable>
            </>
          )}
        </Row>
        {rv.data && rv.data.length > 0 ? (
          <Row gap={10} style={{ alignItems: 'center' }}>
            <Sparkline data={rv.data} width={64} height={20} stroke={rv.stroke} />
            <Txt size={15} weight={FW.semibold} color={rv.color} align="right" style={{ minWidth: 46 }}>
              {rv.text}
            </Txt>
          </Row>
        ) : (
          <Txt size={12} color={C.ink4}>
            {rv.text === '—' ? 'No orders yet' : rv.text}
          </Txt>
        )}
        <View
          style={{
            paddingHorizontal: 9,
            paddingVertical: 3,
            borderRadius: R.full,
            backgroundColor: C.paper2,
            borderWidth: 1,
            borderColor: C.line,
          }}
        >
          <Txt size={11} weight={FW.semibold} color={C.ink3}>
            Priority {rule.priority}
          </Txt>
        </View>
      </View>
    </Pressable>
  );
}

export default function Rules() {
  const nav = useNavigation<Nav>();
  const counts = useCounts();
  const { vertical } = useApp();

  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<RuleFilter>('all');
  // Toggle confirmation flow: which rule is awaiting confirm, and the result.
  const [pendingToggle, setPendingToggle] = useState<Rule | null>(null);
  const [toggleResult, setToggleResult] = useState<{ activated: boolean } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Rule | null>(null);
  const [pendingRestore, setPendingRestore] = useState<Rule | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await listRules();
      setRules(data);
    } catch (e) {
      handleApiError(e, 'Could not load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload on focus (returning from the builder) and when the demo vertical swaps.
  useFocusEffect(
    useCallback(() => {
      load();
    }, [load, vertical]),
  );

  // Live updates: poll while focused so a catalog change (item/category/tag
  // deleted → propagated) auto-deactivates the affected rules WITHOUT a manual
  // refresh. Skips a tick while a confirmation dialog is open so it never
  // changes the list under an action.
  const pollBusyRef = useRef(false);
  // True while an optimistic mutation (toggle/reject/re-activate/delete) is
  // in flight, so a refetch can't clobber it before the write commits — that
  // race made a just-rejected rule snap back into Deactivated.
  const mutatingRef = useRef(false);
  useEffect(() => {
    pollBusyRef.current = !!(pendingToggle || toggleResult || pendingDelete || pendingRestore);
  }, [pendingToggle, toggleResult, pendingDelete, pendingRestore]);
  useFocusEffect(
    useCallback(() => {
      const id = setInterval(() => {
        if (!pollBusyRef.current && !mutatingRef.current) void load();
      }, 8_000);
      return () => clearInterval(id);
    }, [load]),
  );
  // Instant path: refetch the moment a catalog change is pushed (skip if busy).
  useLiveCatalogRefresh(useCallback(() => {
    if (!pollBusyRef.current && !mutatingRef.current) void load();
  }, [load]));

  // Tapping the toggle asks for confirmation first (the Switch only flips once
  // confirmed, since its value is driven by the rule's status).
  const requestToggle = (rule: Rule) => setPendingToggle(rule);

  const confirmToggle = async () => {
    const rule = pendingToggle;
    if (!rule) return;
    const next: RuleStatus = rule.status === 'active' ? 'deactivated' : 'active';
    setPendingToggle(null);
    mutatingRef.current = true;
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, status: next } : r)));
    try {
      if (next === 'active') await activateRule(rule.id);
      else await deactivateRule(rule.id);
      counts.refresh();
      setToggleResult({ activated: next === 'active' });
    } catch (e) {
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, status: rule.status } : r)));
      handleApiError(e, 'Could not update rule');
    } finally {
      mutatingRef.current = false;
    }
  };

  // Soft-delete → move the rule into the Deleted tab (kept in the DB).
  const confirmDelete = async () => {
    const rule = pendingDelete;
    if (!rule) return;
    setPendingDelete(null);
    mutatingRef.current = true;
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, deleted: true, status: 'deactivated' } : r)));
    try {
      await deleteRule(rule.id);
      counts.refresh();
    } catch (e) {
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, deleted: false, status: rule.status } : r)));
      handleApiError(e, 'Could not delete rule');
    } finally {
      mutatingRef.current = false;
    }
  };

  // Re-activate a rejected rule: un-reject AND turn it back on so it returns to
  // the live "All" list (matching the "Re-activate" label). restoreRule clears
  // the rejection; activateRule flips it to active (with overlap/supersession).
  const confirmRestore = async () => {
    const rule = pendingRestore;
    if (!rule) return;
    setPendingRestore(null);
    mutatingRef.current = true;
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, deleted: false, status: 'active' } : r)));
    try {
      await restoreRule(rule.id);
      await activateRule(rule.id);
      counts.refresh();
    } catch (e) {
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, deleted: true, status: rule.status } : r)));
      handleApiError(e, 'Could not re-activate rule');
    } finally {
      mutatingRef.current = false;
    }
  };

  const filtered = rules
    .filter((r) => {
      // "Deleted" shows only soft-deleted rules; every other tab hides them.
      if (filter === 'deleted') {
        if (!r.deleted) return false;
      } else if (r.deleted) {
        return false;
      } else if (filter === 'deactivated') {
        // Turned-off rules (not deleted).
        if (r.status !== 'deactivated') return false;
      } else if (filter === 'manual') {
        // "Created by you" — only hand-authored rules (live, not deactivated).
        if (r.status === 'deactivated') return false;
        if (r.source !== 'manual') return false;
      } else {
        if (r.status === 'deactivated') return false;
        if (filter !== 'all' && motionMeta(r.motion).family !== filter) return false;
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!r.name.toLowerCase().includes(q) && !r.triggerText.toLowerCase().includes(q)) return false;
      }
      return true;
    })
    .sort(
      (a, b) =>
        STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
        b.priority - a.priority ||
        (a.superseded ? 1 : 0) - (b.superseded ? 1 : 0),
    );

  return (
    <Page>
      <PageHeader
        icon="rules-list"
        eyebrow="Upsell engine"
        title="Rules"
        subtitle={
          <>
            Every suggestion SK Upsell can make traces to a rule you approved.{' '}
            {highlightWords('The AI proposes; you decide.', ['AI proposes', 'you decide'])}
          </>
        }
        actions={
          <>
            <HeaderAction label="Let AI propose" icon="sparkle" variant="ai" onPress={() => nav.navigate('Proposals')} />
            <HeaderAction label="New rule" icon="plus" variant="primary" onPress={() => nav.navigate('RuleBuilder')} />
          </>
        }
      />

      {/* Toolbar: premium search */}
      <View style={{ marginTop: 22, marginBottom: 14 }}>
        <SearchBar value={search} onChange={setSearch} />
      </View>

      {/* Single filter row: All · motion families · Deactivated */}
      <Cluster gap={6} style={{ marginBottom: 14, alignItems: 'center' }}>
        {FAMILIES.map((f) => (
          <FilterChip
            key={f.key}
            label={f.label}
            selected={filter === f.key}
            onPress={() => setFilter(f.key)}
            dotColor={f.tone ? toneColors(f.tone).fg : C.ink4}
            height={26}
            paddingX={11}
          />
        ))}
        {/* Created by you — only hand-authored rules */}
        <FilterChip
          label="Created by you"
          selected={filter === 'manual'}
          onPress={() => setFilter('manual')}
          dotColor={C.accent}
          height={26}
          paddingX={11}
        />
        {/* Deactivated rules — same row, after Down-sell */}
        <FilterChip
          label="Deactivated"
          selected={filter === 'deactivated'}
          onPress={() => setFilter('deactivated')}
          dotColor={C.ink4}
          height={26}
          paddingX={11}
        />
        {/* Rejected rules — soft-removed, kept in the DB, restorable */}
        <FilterChip
          label={`Rejected (${rules.filter((r) => r.deleted).length})`}
          selected={filter === 'deleted'}
          onPress={() => setFilter('deleted')}
          dotColor={toneColors('danger').fg}
          height={26}
          paddingX={11}
        />
      </Cluster>

      {loading ? (
        <View style={{ paddingVertical: 56, alignItems: 'center' }}>
          <Spinner size={24} />
        </View>
      ) : rules.length === 0 ? (
        <Empty
          title="No rules yet"
          icon={
            <View
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                backgroundColor: C.accentSoft,
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 14,
              }}
            >
              <Icon name="sparkle" size={24} color={C.accent} />
            </View>
          }
          actions={
            <Row gap={8}>
              <Button label="Read my catalog" variant="accent" onPress={() => nav.navigate('Proposals')} />
              <Button label="Create one manually" onPress={() => nav.navigate('RuleBuilder')} />
            </Row>
          }
        >
          Let SK Upsell read your catalog and propose a starter set — every one is yours to approve before it goes live.
        </Empty>
      ) : (
        <View style={{ gap: 10 }}>
          {filtered.length === 0 ? (
            <View style={{ paddingVertical: 48, alignItems: 'center' }}>
              <Txt size={FS.md} color={C.ink3}>
                No rules match these filters.
              </Txt>
            </View>
          ) : (
            filtered.map((r) => (
              <RuleCard
                key={r.id}
                rule={r}
                onToggle={() => requestToggle(r)}
                onOpen={() => nav.navigate('RuleBuilder', { ruleId: r.id })}
                onDelete={() => setPendingDelete(r)}
                onRestore={() => setPendingRestore(r)}
                restorePending={pendingRestore?.id === r.id}
              />
            ))
          )}
        </View>
      )}

      {/* Confirm before turning a rule on/off */}
      <ConfirmDialog
        visible={!!pendingToggle}
        title={pendingToggle?.status === 'active' ? 'Turn this rule off?' : 'Turn this rule on?'}
        message={
          pendingToggle?.status === 'active'
            ? `"${pendingToggle?.name || 'This rule'}" will stop making suggestions until you turn it back on.`
            : `"${pendingToggle?.name || 'This rule'}" will go live and start making suggestions.`
        }
        confirmText={pendingToggle?.status === 'active' ? 'Turn off' : 'Turn on'}
        cancelText="Cancel"
        confirmColor={pendingToggle?.status === 'active' ? C.danger : C.accent}
        onConfirm={confirmToggle}
        onCancel={() => setPendingToggle(null)}
        onClose={() => setPendingToggle(null)}
      />

      {/* Confirm reject → moves to the Rejected tab (kept in the DB) */}
      <ConfirmDialog
        visible={!!pendingDelete}
        title="Reject this rule?"
        message="It moves to the Rejected tab — you can re-activate it anytime."
        confirmText="Reject"
        cancelText="Cancel"
        confirmColor={C.danger}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        onClose={() => setPendingDelete(null)}
      />

      {/* Confirm re-activate → back live in the All list */}
      <ConfirmDialog
        visible={!!pendingRestore}
        title="Re-activate this rule?"
        message="It goes back live in your All list and starts making suggestions again."
        confirmText="Re-activate"
        cancelText="Cancel"
        confirmColor={C.accent}
        onConfirm={confirmRestore}
        onCancel={() => setPendingRestore(null)}
        onClose={() => setPendingRestore(null)}
      />

      {/* Success after the toggle lands */}
      <SuccessDialog
        visible={!!toggleResult}
        title={toggleResult?.activated ? 'Rule activated' : 'Rule turned off'}
        message={
          toggleResult?.activated
            ? 'It’s now live and making suggestions.'
            : 'It’s paused — no suggestions until you turn it back on.'
        }
        primaryAction={{ label: 'Done', onPress: () => setToggleResult(null) }}
        onClose={() => setToggleResult(null)}
      />
    </Page>
  );
}
